import { useState, useEffect } from "react";

interface OptimizedHeroImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean; // For above-the-fold hero images
  aspectRatio?: string; // e.g., "16/9", "4/3", "3/2"
  overlay?: React.ReactNode;
  overlayClassName?: string;
}

/**
 * OptimizedHeroImage Component
 * Provides optimized image loading with:
 * - Responsive sizing with srcset simulation
 * - Lazy loading for below-fold images
 * - Eager loading + preload hints for above-fold heroes
 * - Blur placeholder while loading
 * - Proper aspect ratio to prevent CLS
 */
const OptimizedHeroImage = ({
  src,
  alt,
  className = "",
  priority = false,
  aspectRatio = "16/9",
  overlay,
  overlayClassName = "",
}: OptimizedHeroImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "200px", // Start loading 200px before visible
        threshold: 0.01,
      }
    );

    const element = document.getElementById(`hero-${src.replace(/[^a-zA-Z0-9]/g, '')}`);
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [src, priority]);

  // Preload priority images
  useEffect(() => {
    if (priority && typeof window !== 'undefined') {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [src, priority]);

  const imageId = `hero-${src.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div
      id={imageId}
      className={`relative overflow-hidden ${className}`}
      style={{ aspectRatio }}
    >
      {/* Blur placeholder */}
      <div
        className={`absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 transition-opacity duration-500 ${
          isLoaded ? "opacity-0" : "opacity-100"
        }`}
        style={{
          backdropFilter: "blur(20px)",
        }}
      />

      {/* Actual image */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw"
        />
      )}

      {/* Optional overlay */}
      {overlay && (
        <div className={`absolute inset-0 ${overlayClassName}`}>
          {overlay}
        </div>
      )}
    </div>
  );
};

export default OptimizedHeroImage;
